import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';

import AnonymousRoute from './AnonymousRoute';
import PublicLayout from 'routes/PublicLayout';
import { SuspendLayout } from 'components/main-layout';

import { createSuspenseRoute, SECURE_CONFIG } from 'utils/SecureRoute';
import { isAuthed } from 'utils/common/session';

import { waitingLoader } from 'pages/waiting/test/waitingLoader';
import { getBasicInfoLoader } from 'pages/get-basic-info/getBasicInfoLoader';
import { eulaLoader } from 'pages/eula/eulaLoader';
import { rivirTCLoader } from 'pages/eula/check-rivir-tc/rivirTCLoader';
import { renewalNotificationLoader } from 'pages/renewal-notification/renewalNotificationLoader';
import { landingPageLoader } from 'pages/landingpage/landingPageLoader';
import { loginOtpLoader } from 'pages/login-otp/loginOtpLoader';
import { changePasswordUnlockLoader } from 'pages/change-password-unlock/changePasswordUnlockLoader';
import { personalizationLoader } from 'pages/personalization/personalizationLoader';
import { messagesLoader } from 'pages/messages/messagesLoader';
import { loginLoader } from 'pages/login/loginLoader';
import { creditCardInfoLoader } from 'pages/check-credit-card-info/creditCardInfoLoader';

import { isProdEnv } from 'utils/checkDeployEnv';
import { blankLoader } from 'pages/blank/blankLoader';
import { productDetailRoutes } from 'pages/product-full-view';
import BrandedMember, {
  memberRoutes,
  memberSiblingRoutes,
} from 'pages/branded-members';

import { securityMatrixNestedRoutes, securityNestedRoutes } from 'pages/role';
import { workflowRoutes, workflowsRoutes } from 'pages/workflow';
import {
  maintenanceFormNestedRoutes,
  maintenanceFormSiblingRoutes,
} from 'pages/maintenance-form';
import { transitionPageNestedRoutes } from 'pages/transition-page';
import { homePageLoader } from 'pages/home/homePageLoader';
import { checkAuthorizationLoader } from 'pages/check-authorization/checkAuthorizationLoader';
import { preRoutingLoader } from 'pages/pre-routing/preRoutingLoader';

// Lazy-loaded page components
const HomePage = lazy(() => import('pages/home'));
const LogIn = lazy(() => import('pages/login'));
const LoginOtp = lazy(() => import('pages/login-otp'));
const Register = lazy(() => import('pages/register'));
const ForgotPassword = lazy(() => import('pages/resetpassword'));
const Eula = lazy(() => import('pages/eula'));
const EulaRivirTC = lazy(() => import('pages/eula/check-rivir-tc/RivirTc'));
const CreditCardEulaPage = lazy(() => import('pages/eula/CreditCardEulaPage'));
const RenewalNotification = lazy(() => import('pages/renewal-notification'));
const SplashScreen = lazy(() => import('pages/waiting'));
const NotFound = lazy(() => import('pages/error/NotFound'));
const LoadingPage = lazy(() => import('pages/loadingPage'));
const InternalServer = lazy(() => import('pages/error/InternalSever'));
const Timeout = lazy(() => import('pages/error/Timeout'));
const SamplePage = lazy(() => import('pages/sample'));
const MemberInformationSheet = lazy(
  () => import('pages/member-information-sheet')
);
const Trans = lazy(() => import('pages/transition-page-blueprint'));
const SSOLanding = lazy(() => import('pages/sso-landing'));
const LoadingValidate = lazy(() => import('pages/sso-landing/LoadingValidate'));
const EulaAccepted = lazy(() => import('pages/sso-landing/EulaAccepted'));
const ErrorValidateRivir = lazy(
  () => import('pages/sso-landing/ErrorValidateRivir')
);
const WelcomeTransition = lazy(
  () => import('pages/sso-landing/WelcomeTransition')
);
const SsoProduct = lazy(() => import('pages/sso-product'));
const PostValidationSSOProduct = lazy(
  () => import('pages/sso-landing/PostValidationSSOProduct')
);
const ExternalMeetingRoom = lazy(() => import('pages/external-meeting'));
const GetBasicInfo = lazy(() => import('pages/get-basic-info'));
const ChangePasswordUnlock = lazy(() => import('pages/change-password-unlock'));
const UnlockAccount = lazy(() => import('pages/unlock-account'));
const CheckCreditCardInfo = lazy(() => import('pages/check-credit-card-info'));
const CheckAuthorization = lazy(() => import('pages/check-authorization'));
const Personalizer = lazy(() => import('pages/personalization'));
const LandingPage = lazy(() => import('pages/landingpage'));
const Notification = lazy(() => import('pages/messages'));
const PreRouting = lazy(() => import('pages/pre-routing'));
const InteropCallbackAuthentication = lazy(
  () => import('pages/interop-callback/InteropCallbackAuthentication')
);

// HomeRoutes components
const Dashboard = lazy(() => import('pages/dashboard'));
const BlankPage = lazy(() => import('pages/blank'));
const Products = lazy(() => import('pages/branded-products'));
const Queries = lazy(() => import('pages/favorite-queries'));
const QueriesShared = lazy(() => import('pages/favorite-queries/shared'));
const MemberMaintenanceCompany = lazy(
  () => import('pages/member-maintenance/components/MemberMaintenanceCompany')
);
const CompanyProfile = lazy(() => import('pages/company-profile'));
const AssetFullView = lazy(() => import('pages/asset-full-view'));
const AssetCreate = lazy(() => import('pages/asset-full-view/creation'));
const AssetIframeCreate = lazy(() => import('pages/asset-full-view/iframe'));
const UserProfile = lazy(() => import('pages/user-profile'));
const UserProfileCompany = lazy(
  () => import('pages/company/components/user-profile/UserProfileCompany')
);
const Company = lazy(() => import('pages/company'));
const AssetVersions = lazy(() => import('pages/asset-versions'));
const BrandedEntities = lazy(() => import('pages/branded'));
const Apl = lazy(() => import('pages/branded-products-apl'));
const Syndication = lazy(() => import('pages/branded-products-syndication'));

const MemberGridMain = lazy(
  () => import('pages/branded-members/views/member-grid-main/MemberGridMain')
);
const ProductsForMember = lazy(
  () => import('pages/branded-members/components/products-for-member')
);
const Programs = lazy(() => import('pages/branded-programs'));
const Reportings = lazy(() => import('pages/branded-reporting'));
const ReportingOwned = lazy(() => import('pages/branded-reporting/owned'));
const ReportingShared = lazy(() => import('pages/branded-reporting/shared'));
const Reporting = lazy(() => import('pages/reporting'));
const Folders = lazy(() => import('pages/folders'));
const OwnedFolders = lazy(() => import('pages/folders/folders-owned'));
const SharedFolders = lazy(() => import('pages/folders/folders-shared'));
const FolderDetails = lazy(() => import('pages/folders/folder-details'));
const HelpMaintenanceCategory = lazy(
  () => import('pages/maintenance/components/help/category')
);
const PostGridView = lazy(
  () => import('pages/maintenance/components/help/post')
);
const Articles = lazy(() => import('pages/articles'));
const ArticleFullView = lazy(
  () => import('pages/articles/components/fullview')
);
const PostFullView = lazy(
  () => import('pages/maintenance/components/help/post/fullview')
);
const EulaMaintenance = lazy(() => import('pages/eula-maintenance'));
const MemberMaintenance = lazy(() => import('pages/member-maintenance'));
const UserMaintenance = lazy(() => import('pages/user-maintenance'));
const EulaContainer = lazy(
  () => import('pages/eula-maintenance/components/form')
);

//TODO: update layout
const RivirTC = lazy(() => import('pages/rivir-tc'));
const RivirTCDetailForm = lazy(
  () => import('pages/rivir-tc/form/RivirTCDetailForm')
);

const RenewalNotificationMaintenance = lazy(
  () => import('pages/renewal-notification-maintenance')
);
const RenewalNotificationDetailForm = lazy(
  () =>
    import('pages/renewal-notification-maintenance/form/RenewalNotificationDetailForm')
);

const SSOApprovalMemberPage = lazy(
  () => import('pages/sso-approval/SSOApprovalMember')
);
const SSOApprovalUserPage = lazy(
  () => import('pages/sso-approval/SSOApprovalUser')
);
const DigitalMedia = lazy(() => import('pages/branded-assets'));
const DigitalMediaAssets = lazy(() => import('pages/branded-assets-normal'));
const DigitalMediaDocument = lazy(
  () => import('pages/branded-assets-document')
);
const DigitalMediaMultiMedia = lazy(
  () => import('pages/branded-assets-multi-media')
);
const ManageShared = lazy(() => import('pages/manage-shared'));
const CommunicationTemplate = lazy(
  () => import('pages/communication-template')
);
const CommunicationTemplateFullView = lazy(
  () => import('pages/communication-template/components/fullview')
);
const Workflow = lazy(() => import('pages/workflow'));
const WorkflowTemplateFullView = lazy(
  () => import('pages/workflow-template-full')
);
const WorkflowAdminstration = lazy(
  () => import('pages/workflow-adminstration')
);
const WorkflowTemplate = lazy(() => import('pages/workflow-template'));
const ProductFullView = lazy(() => import('pages/product-full-view'));
const ProductSyndicationHistory = lazy(
  () => import('pages/product-full-view/view/syndication-history')
);
const ProductOverlays = lazy(
  () => import('pages/product-full-view/view/product-overlays/ProductOverlays')
);
const IngredientCompliance = lazy(() => import('pages/ingredient-compliance'));
const ProductDownloadSheets = lazy(
  () => import('pages/product-full-view/view/ProductDownloadSheets')
);
const ProductAplDetail = lazy(() => import('pages/product-apl-detail'));
const CuratedQuery = lazy(
  () => import('pages/markup-maintenance/components/curated-query/CuratedQuery')
);
const RecipientsFields = lazy(() => import('pages/recipients-fields'));
const MySubscriptions = lazy(
  () => import('pages/subscription/components/MySubscriptions')
);
const SubscriptionsTo = lazy(
  () => import('pages/subscription/components/SubscriptionsTo')
);
const MappingList = lazy(() => import('pages/mapping'));
const MappingConfig = lazy(
  () => import('pages/mapping/components/mapping-config')
);
const Publications = lazy(() => import('pages/publication'));
const Role = lazy(() => import('pages/role'));
const Meeting = lazy(() => import('pages/meeting'));
const TransitionPage = lazy(() => import('pages/transition-page'));
const MfaVerificationLog = lazy(() => import('pages/mfa-verification-log'));
const Gs1RevenueDashboard = lazy(() => import('pages/gs1-revenue-dashboard'));
const CompanySharingReport = lazy(() => import('pages/company-sharing-report'));
const MaintenanceForm = lazy(() => import('pages/maintenance-form'));
const Preview360Component = lazy(() => import('common/components/360-preview'));
const MinimumRequirements = lazy(() => import('pages/minimum-requirements'));
const EulaCreditCard = lazy(() => import('pages/eula-credit-card'));
const CreditCardEulaFormContainer = lazy(
  () => import('pages/eula-credit-card/components/CreditCardEulaFormContainer')
);
const DataSyncTimeTracking = lazy(
  () => import('pages/data-sync-time-tracking')
);
const ExecuteQuery = lazy(() => import('pages/execute-query'));
const QcTimeTracking = lazy(() => import('pages/qc-time-tracking'));

const TicketingSystem = lazy(() => import('pages/ticketing-system'));
const TicketFullView = lazy(() => import('pages/ticket-full-view'));
const TicketingGroup = lazy(() => import('pages/ticketing-group'));
const TicketingGroupMember = lazy(
  () => import('pages/ticketing-group/view/TicketingGroupMember')
);

const GtinAllocation = lazy(() => import('pages/gs1-gtin/GtinAllocation'));
const GtinIdentifiersPage = lazy(
  () => import('pages/gs1-gtin/GtinIdentifiersPage')
);
const GtinOrderHistoryPage = lazy(
  () => import('pages/gs1-gtin/GtinOrderHistoryPage')
);

export const createRoutesConfig = ({
  returnUrl,
  queryClient,
  ability,
} = {}) => {
  const isProductionEnv = isProdEnv();

  return [
    {
      path: '/',
      element: <PublicLayout />,
      children: [
        { path: 'refreshing', element: null },
        { path: 'login', element: <LogIn />, loader: loginLoader },
        { path: 'register', element: <Register /> },
        { path: 'forgot', element: <ForgotPassword /> },
        { path: 'notfound', element: <NotFound /> },
        { path: '500-error', element: <InternalServer /> },
        { path: 'timeout', element: <Timeout /> },
        { path: 'sample', element: <SamplePage /> },
        {
          path: 'sheet/:memberId/:privateKey',
          element: <MemberInformationSheet />,
        },
        { path: 'sso-test', element: <Trans /> },
        { path: 'sso', element: <SSOLanding /> },
        {
          path: 'dropbox/callback',
          element: <InteropCallbackAuthentication />,
        },
        { path: 'validate', element: <LoadingValidate /> },
        { path: 'eula-page', element: <EulaAccepted /> },
        {
          path: 'error-validate-rivir',
          element: (
            <ErrorValidateRivir
              title='Declined the EULA'
              content='Usage requires EULA acceptance'
            />
          ),
        },
        {
          path: 'error-invalid-token',
          element: <ErrorValidateRivir title='Token is expired' />,
        },
        { path: 'welcome', element: <WelcomeTransition /> },
        { path: 'product/createnew', element: <SsoProduct /> },
        {
          path: 'post-validate-sso-product',
          element: <PostValidationSSOProduct />,
        },
        {
          path: 'ext-meeting/:meetingId',
          element: <AnonymousRoute component={ExternalMeetingRoom} />,
        },
        {
          path: 'login-otp',
          element: <LoginOtp />,
          loader: loginOtpLoader,
        },
        { path: 'unlock-account', element: <UnlockAccount /> },
      ],
    },
    { path: 'eula', element: <Eula />, loader: eulaLoader(queryClient) },
    {
      path: 'check-rivir-tc',
      element: <EulaRivirTC />,
      loader: rivirTCLoader(queryClient),
    },
    { path: 'cc-eula', element: <CreditCardEulaPage /> },
    {
      path: 'renewal-notification',
      element: <RenewalNotification />,
      loader: renewalNotificationLoader(queryClient),
    },
    {
      path: 'waiting',
      element: <SplashScreen />,
      loader: waitingLoader,
    },
    {
      path: 'personalization',
      element: (
        <PrivateRoute validateFn={isAuthed}>
          <Personalizer />
        </PrivateRoute>
      ),
      loader: personalizationLoader(queryClient),
    },
    {
      path: 'landingPage',
      element: (
        <PrivateRoute validateFn={isAuthed} returnUrl={returnUrl}>
          <LandingPage />
        </PrivateRoute>
      ),
      loader: landingPageLoader(queryClient),
    },

    {
      path: 'get-basic-info',
      element: <GetBasicInfo />,
      loader: getBasicInfoLoader(queryClient),
    },
    {
      path: 'check-authorization',
      element: (
        <PrivateRoute validateFn={isAuthed} returnUrl={returnUrl}>
          <CheckAuthorization />
        </PrivateRoute>
      ),
      loader: checkAuthorizationLoader,
    },
    {
      path: 'change-password',
      element: (
        <PrivateRoute validateFn={isAuthed} returnUrl={returnUrl}>
          <ChangePasswordUnlock />
        </PrivateRoute>
      ),
      loader: changePasswordUnlockLoader(queryClient),
    },
    {
      path: 'notification',
      element: (
        <PrivateRoute validateFn={isAuthed}>
          <Notification />
        </PrivateRoute>
      ),
      loader: messagesLoader,
    },
    {
      path: 'pre-routing',
      element: (
        <PrivateRoute validateFn={isAuthed}>
          <PreRouting />
        </PrivateRoute>
      ),
      loader: preRoutingLoader(queryClient),
    },
    {
      path: '/',
      element: (
        <PrivateRoute validateFn={isAuthed}>
          <HomePage />
        </PrivateRoute>
      ),
      loader: homePageLoader(queryClient, ability),
      shouldRevalidate: ({ currentUrl, nextUrl }) => {
        return currentUrl?.pathname !== nextUrl.pathname;
      },
      children: [
        // ADD INDEX ROUTE TO REDIRECT TO DASHBOARD
        {
          index: true,
          element: <Navigate to='/dashboard' replace />,
        },
        {
          path: 'dashboard',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: Dashboard,
          }),
        },
        { path: 'blank', element: <BlankPage />, loader: blankLoader },

        // Favorite queries
        {
          path: 'favorite/queries',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: Queries,
          }),
        },
        {
          path: 'favorite/sharedqueries',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: QueriesShared,
          }),
        },
        {
          path: 'favorite/favorite-products',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Products,
          }),
        },
        {
          path: 'favorite/favorite-assets',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: DigitalMedia,
            componentProps: {
              mediaType: 'favoriteAssetPage',
            },
          }),
        },
        {
          path: 'favorite/favorite-folders',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.FOLDER,
            Component: Folders,
          }),
        },
        {
          path: 'favorite/favorite-members',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MEMBER,
            Component: MemberGridMain,
          }),
        },

        // User profiles
        {
          path: 'my-profile',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: UserProfile,
            componentProps: {
              editable: true,
            },
          }),
        },
        {
          path: 'user-profile/:id',
          element: createSuspenseRoute({
            secureConfig: [SECURE_CONFIG.USER, SECURE_CONFIG.COMPANY_PROFILE],
            Component: UserProfileCompany,
            componentProps: {
              editable: true,
            },
          }),
        },
        {
          path: 'user/:id',
          element: createSuspenseRoute({
            secureConfig: [SECURE_CONFIG.USER, SECURE_CONFIG.COMPANY_PROFILE],
            Component: UserProfile,
            componentProps: {
              editable: true,
            },
          }),
        },

        // Company and member management
        {
          path: 'member-management-company/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_MEMBER,
            Component: MemberMaintenanceCompany,
          }),
        },
        {
          path: 'company',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.COMPANY_PROFILE,
            Component: CompanyProfile,
          }),
        },
        {
          path: 'company/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.COMPANY_PROFILE,
            Component: Company,
          }),
        },
        {
          path: 'company/edit/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EDIT_MEMBER,
            Component: MemberMaintenanceCompany,
          }),
        },

        // Maintenance forms
        {
          path: 'maintenance/form',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_FORM_MANAGEMENT,
            Component: MaintenanceForm,
          }),
          children: maintenanceFormNestedRoutes,
        },

        // Add the sibling routes (these need the MaintenanceForm setup too)
        {
          path: 'exported-splash-forms',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: MaintenanceForm,
          }),
          children: [
            {
              index: true,
              element: maintenanceFormSiblingRoutes[0].element,
            },
          ],
        },
        {
          path: 'history-splash-form/:entityType/:entityId',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: MaintenanceForm,
          }),
          children: [
            {
              index: true,
              element: maintenanceFormSiblingRoutes[1].element,
            },
          ],
        },

        // Security
        {
          path: 'security',
          element: (
            <SuspendLayout>
              <Role />
            </SuspendLayout>
          ), // This handles reducer/saga injection and initialization
          children: securityNestedRoutes,
        },

        // Security Matrix with nested routes
        {
          path: 'security-matrix',
          element: (
            <SuspendLayout>
              <Role />
            </SuspendLayout>
          ),
          children: securityMatrixNestedRoutes,
        },

        // Maintenance pages
        {
          path: 'maintenance/transition-page',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_TRANSITION_PAGE,
            Component: TransitionPage,
          }),
          children: transitionPageNestedRoutes,
        },
        {
          path: 'maintenance/company-sharing-report',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_REPORT_SECTION,
            Component: CompanySharingReport,
          }),
        },
        {
          path: 'maintenance/mfa-verification-log',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_MFA_VERIFICATION,
            Component: MfaVerificationLog,
          }),
        },
        {
          path: 'maintenance/gs1-revenue-dashboard',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.SUPER_ADMIN,
            Component: Gs1RevenueDashboard,
          }),
        },

        // Branded entities
        {
          path: 'all',
          element: <BrandedEntities />,
        },

        // Products main pages and details
        {
          path: 'products',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Products,
          }),
        },
        {
          path: 'product/sheets/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductDownloadSheets,
          }),
        },
        {
          path: 'product/syndication-history/:id',
          element: createSuspenseRoute({
            secureConfig: [
              SECURE_CONFIG.PRODUCT,
              SECURE_CONFIG.ACCESS_SYNDICATION,
              SECURE_CONFIG.ACCESS_GDSN,
            ],
            Component: ProductSyndicationHistory,
            operator: 'and',
          }),
        },
        {
          path: 'products/syndication-history',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductSyndicationHistory,
          }),
        },
        {
          path: 'products/product-overlays',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductOverlays,
          }),
        },
        {
          path: 'products/ingredient-compliance',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.INGREDIENT_COMPLIANCE,
            Component: IngredientCompliance,
          }),
        },
        {
          path: 'products/member/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductsForMember,
          }),
        },
        {
          path: 'products/apl',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Apl,
          }),
        },
        {
          path: 'products/apl/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductAplDetail,
          }),
        },
        {
          path: 'products/syndication',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Syndication,
          }),
        },
        {
          path: 'products/gdsn-received-products',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Publications,
          }),
        },
        {
          path: 'products/:actionType',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: Products,
          }),
        },
        {
          path: 'products/gtin-allocation',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: GtinAllocation,
          }),
          children: [
            {
              index: true,
              element: createSuspenseRoute({
                secureConfig: SECURE_CONFIG.MANAGE_ORDER,
                Component: GtinIdentifiersPage,
              }),
            },
            {
              path: 'order-history',
              element: createSuspenseRoute({
                secureConfig: SECURE_CONFIG.MANAGE_ORDER,
                Component: GtinOrderHistoryPage,
              }),
            },
          ],
        },

        // Subscriptions
        {
          path: 'subscriptions/my-subscriptions',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: MySubscriptions,
          }),
        },
        {
          path: 'subscriptions/subscriptions-to',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: SubscriptionsTo,
          }),
        },

        // Mappings
        {
          path: 'products/mappings',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_PIM_MAPPING,
            Component: MappingList,
          }),
        },
        {
          path: 'products/mapping/config/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: MappingConfig,
          }),
        },

        // Members
        {
          path: 'members',
          element: <BrandedMember />, // This injects reducer and saga
          children: memberRoutes, // These are the nested routes
        },
        ...memberSiblingRoutes,

        // Digital Media
        {
          path: 'digital-media',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: DigitalMedia,
          }),
        },
        {
          path: 'digital-media/assets',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: DigitalMediaAssets,
          }),
        },
        {
          path: 'digital-media/documents',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: DigitalMediaDocument,
          }),
        },
        {
          path: 'digital-media/multi-media',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: DigitalMediaMultiMedia,
          }),
        },

        // Asset management
        {
          path: 'asset/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET_CREATE,
            Component: AssetCreate,
          }),
        },
        {
          path: 'asset/create-iframe',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET_CREATE,
            Component: AssetIframeCreate,
          }),
        },
        {
          path: 'asset/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: AssetFullView,
          }),
        },
        {
          path: 'asset/versions/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ASSET,
            Component: AssetVersions,
          }),
        },

        // Reportings
        {
          path: 'reportings',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.REPORTING,
            Component: Reportings,
          }),
        },
        {
          path: 'reportings/owned',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.REPORTING,
            Component: ReportingOwned,
          }),
        },
        {
          path: 'reportings/shared',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.REPORTING,
            Component: ReportingShared,
          }),
        },
        {
          path: 'reporting/:reportingId',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.REPORTING,
            Component: Reporting,
          }),
        },
        {
          path: 'reporting/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.REPORTING_CREATE,
            Component: Reporting,
          }),
        },

        // Programs
        {
          path: 'programs',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: Programs,
          }),
        },

        // Folders
        {
          path: 'folders',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.FOLDER,
            Component: Folders,
          }),
        },
        {
          path: 'folders/owned',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.FOLDER,
            Component: OwnedFolders,
          }),
        },
        {
          path: 'folders/shared',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.FOLDER,
            Component: SharedFolders,
          }),
        },
        {
          path: 'folder/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.FOLDER,
            Component: FolderDetails,
          }),
        },

        // Help and maintenance
        {
          path: 'maintenance/help/category',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.HELP_CATEGORY,
            Component: HelpMaintenanceCategory,
          }),
        },
        {
          path: 'maintenance/help/post',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.HELP_POST,
            Component: PostGridView,
          }),
        },
        {
          path: 'maintenance/help/articles',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ARTICLE,
            Component: Articles,
          }),
        },
        {
          path: 'maintenance/help/articles/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ARTICLE_MANAGE,
            Component: ArticleFullView,
          }),
        },
        {
          path: 'maintenance/help/articles/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ARTICLE,
            Component: ArticleFullView,
          }),
        },
        {
          path: 'maintenance/help/post/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.HELP_POST_MANAGE,
            Component: PostFullView,
          }),
        },
        {
          path: 'maintenance/help/post/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.HELP_POST,
            Component: PostFullView,
          }),
        },

        // EULA maintenance
        {
          path: 'maintenance/eula/list',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA,
            Component: EulaMaintenance,
          }),
        },
        {
          path: 'maintenance/eula/credit-card',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA,
            Component: EulaCreditCard,
          }),
        },
        {
          path: 'maintenance/eula/credit-card/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA_MANAGE,
            Component: CreditCardEulaFormContainer,
            componentProps: {
              actionType: 'create',
            },
          }),
        },
        {
          path: 'maintenance/eula/credit-card/clone/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA_MANAGE,
            Component: CreditCardEulaFormContainer,
            componentProps: {
              actionType: 'clone',
            },
          }),
        },
        {
          path: 'maintenance/eula/rivir-tc/list',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RIVIR_TC,
            Component: RivirTC,
          }),
        },
        {
          path: 'maintenance/eula/rivir-tc/clone/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RIVIR_TC,
            Component: RivirTCDetailForm,
          }),
        },
        {
          path: 'maintenance/eula/rivir-tc/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RIVIR_TC,
            Component: RivirTCDetailForm,
          }),
        },
        {
          path: 'maintenance/eula/renewal-notification/list',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RENEWAL_NOTIFICATION,
            Component: RenewalNotificationMaintenance,
          }),
        },
        {
          path: 'maintenance/eula/renewal-notification/clone/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RENEWAL_NOTIFICATION,
            Component: RenewalNotificationDetailForm,
          }),
        },
        {
          path: 'maintenance/eula/renewal-notification/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.VIEW_RENEWAL_NOTIFICATION,
            Component: RenewalNotificationDetailForm,
          }),
        },
        {
          path: 'maintenance/eula/clone/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA_MANAGE,
            Component: EulaContainer,
          }),
        },
        {
          path: 'maintenance/eula/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.EULA_MANAGE,
            Component: EulaContainer,
          }),
        },

        // Member and user management
        {
          path: 'maintenance/member-management/:memberId?',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_MEMBER_MANAGEMENT,
            Component: MemberMaintenance,
          }),
        },
        {
          path: 'maintenance/user-management/:userId?',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_USER_MANAGEMENT,
            Component: UserMaintenance,
          }),
        },

        // Tracking
        {
          path: 'maintenance/data-sync-activity-tracking',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_BILLABLE_TRACKING,
            Component: DataSyncTimeTracking,
          }),
        },
        {
          path: 'maintenance/qc-time-tracking',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_BILLABLE_TRACKING,
            Component: QcTimeTracking,
          }),
        },

        // Shared management
        {
          path: 'manage-shared',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.SHARED_ENTITY,
            Component: ManageShared,
          }),
        },

        // Loading page
        {
          path: 'loading',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: LoadingPage,
          }),
        },

        // Communication templates
        {
          path: 'communication-templates',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.COMMUNICATION_TEMPLATE,
            Component: CommunicationTemplate,
          }),
        },
        {
          path: 'communication-templates/create',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.COMMUNICATION_TEMPLATE_MANAGE,
            Component: CommunicationTemplateFullView,
          }),
        },
        {
          path: 'communication-templates/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.COMMUNICATION_TEMPLATE,
            Component: CommunicationTemplateFullView,
          }),
        },

        // Workflows
        {
          path: 'workflows',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.WORKFLOW,
            Component: Workflow,
          }),
          children: workflowsRoutes,
        },
        {
          path: 'workflow',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.WORKFLOW,
            Component: Workflow,
          }),
          children: workflowRoutes,
        },
        {
          path: 'workflow-maintenance/administration',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.WORKFLOW_ADMINISTRATION,
            Component: WorkflowAdminstration,
          }),
        },
        {
          path: 'workflow-maintenance/definition',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.WORKFLOW_DEFINITION,
            Component: WorkflowTemplate,
          }),
        },
        {
          path: 'workflow-maintenance/definition/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: WorkflowTemplateFullView,
          }),
        },

        // Curated query
        {
          path: 'curated-query',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.CURATED_QUERY,
            Component: CuratedQuery,
          }),
        },

        // Ticket System
        {
          path: 'ticketing-system',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.TICKET_SYSTEM,
            Component: TicketingSystem,
          }),
        },
        {
          path: 'ticketing-system/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.TICKET_SYSTEM,
            Component: TicketFullView,
          }),
        },
        {
          path: 'ticket-group-assignment/member/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.SUPER_ADMIN,
            Component: TicketingGroupMember,
          }),
        },
        {
          path: 'ticket-group-assignment',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.TICKET_GROUP,
            Component: TicketingGroup,
          }),
        },

        // Recipients fields
        {
          path: 'recipients-fields',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.RECIPIENTS_FIELDS,
            Component: RecipientsFields,
          }),
        },
        // Meeting
        {
          path: 'meeting',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: Meeting,
          }),
        },

        // SSO approval
        {
          path: 'sso-approval/member',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_SSO_MEMBER_APPROVAL,
            Component: SSOApprovalMemberPage,
          }),
        },
        {
          path: 'sso-approval/user',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_SSO_USER_APPROVAL,
            Component: SSOApprovalUserPage,
          }),
        },

        // Execute query
        {
          path: 'execute-query/:id',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: ExecuteQuery,
          }),
        },

        // Minimum requirements
        {
          path: 'maintenance/minimum-requirements',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.MANAGE_MINIMUM_REQUIREMENTS,
            Component: MinimumRequirements,
          }),
        },

        // Product views
        {
          path: 'product',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.PRODUCT,
            Component: ProductFullView,
          }),
          children: productDetailRoutes(queryClient),
        },

        // Preview 360
        {
          path: 'preview/360',
          element: createSuspenseRoute({
            secureConfig: SECURE_CONFIG.ALL_AUTH,
            Component: () => (
              <div style={{ width: 500, height: 500 }}>
                <Preview360Component mockData={true} />
              </div>
            ),
          }),
        },
      ],
    },

    ...(isProductionEnv
      ? []
      : [
          {
            path: 'check-credit-card-info',
            element: (
              <PrivateRoute validateFn={isAuthed} returnUrl={returnUrl}>
                <CheckCreditCardInfo />
              </PrivateRoute>
            ),
            loader: creditCardInfoLoader(queryClient, ability),
          },
        ]),
    { path: '*', element: <Navigate to='/dashboard' replace /> },
  ];
};
